/**
 * 시간 파싱 관련 유틸 함수 모듈
 * @module core/time/parse
 */
import dayjs, { ManipulateType } from 'dayjs';
import { isNil } from 'lodash-es';
import { UTC_TIME_FORMAT, GLOBAL_TIME_RANGE_FORMAT, GLOBAL_TIME_LIVE_FORMAT } from './constants';

/**
 * 인터벌 파싱 결과
 */
export interface ParsedInterval {
  offset: number;
  unit: ManipulateType;
}

/**
 * 기간 파싱 결과
 */
export interface ParsedPeriod {
  offset: number;
  unit: 's';
}

/**
 * 시간 문자열에서 포맷을 자동 감지
 * @param time 시간 문자열
 * @returns 매칭되는 포맷 또는 undefined
 */
export const returnMatchingFormat = (time: string | null | undefined): string | undefined => {
  if (isNil(time)) {
    return undefined;
  }
  if (time.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/)) {
    return UTC_TIME_FORMAT;
  }
  if (time.match(/^\d{2}.\d{2}.\d{2} \d{2}:\d{2}:\d{2}$/)) {
    return GLOBAL_TIME_RANGE_FORMAT;
  }
  if (time.match(/^\d{4}.\d{2}.\d{2} \d{2}:\d{2}:\d{2}$/)) {
    return GLOBAL_TIME_LIVE_FORMAT;
  }
  return undefined;
};

/**
 * 인터벌 문자열 파싱 (예: 'I5s', 'p5s', '1h 30m')
 * @param period 인터벌 문자열
 * @returns offset과 unit
 */
export const parseInterval = (period: string): ParsedInterval => {
  if (period.split(' ').length > 1) {
    const [hours, minutes] = period.split(' ');
    return {
      offset: +hours.slice(1, -1) * 60 + +minutes.slice(0, -1),
      unit: minutes.slice(-1) as ManipulateType,
    };
  }

  return {
    offset: +period.slice(1, -1),
    unit: period.slice(-1) as ManipulateType,
  };
};

/**
 * 기간 문자열 파싱 (예: 'p5m', 'p1h', 'p1d 2h 30m')
 * @param period 기간 문자열
 * @returns offset (초 단위)과 unit
 */
export const parsePeriod = (period: string): ParsedPeriod => {
  const timeParts = period.split(' ');
  let offset = 0;

  timeParts.forEach((part) => {
    let refinedPart = part;

    if (part.includes('customp')) {
      refinedPart = part.slice(7);
    } else if (part.includes('p')) {
      refinedPart = part.slice(1);
    }

    const value = parseInt(refinedPart, 10);
    if (part.includes('d')) {
      offset += value * 60 * 60 * 24;
    } else if (part.includes('h')) {
      offset += value * 60 * 60;
    } else if (part.includes('m')) {
      offset += value * 60;
    } else if (part.includes('s')) {
      offset += value;
    } else if (part.includes('w')) {
      offset += value * 60 * 60 * 24 * 7;
    } else if (part.includes('M')) {
      const now = dayjs.tz();
      const targetDate = now.subtract(value, 'month');
      const diffInSeconds = now.diff(targetDate, 'second');
      offset += diffInSeconds;
    }
  });

  return {
    offset,
    unit: 's',
  };
};

/**
 * 기간 문자열을 파츠로 파싱 (예: 'p1d' -> [1, 'd'])
 * @param period 기간 문자열
 * @returns [값, 단위] 또는 null
 */
export const parsePeriodParts = (period: string): [number, ManipulateType] | null => {
  const [value, unit] = /^p([0-9]*)([a-z])$/.exec(period || '')?.slice(1) || [];
  return isNil(value) || isNil(unit) ? null : [+value, unit as ManipulateType];
};
